// Builds the layered PSD and the flattened order PNG from the live card-art-layer
// DOM (background + each placed sprite + the card frame), so the designer gets a
// full-card file to open in Photoshop. Both exports intentionally use the clean,
// non-watermarked background + frame art (see CARD_FRAME_FULL_SRC and each
// BACKGROUNDS entry's `fullId` in App.js) even though the live on-screen preview
// keeps showing the watermarked versions.
import { writePsd } from 'ag-psd';
import { fmtName, CARD_W, FULL_CARD_W, FULL_CARD_H, ART_WINDOW_LEFT, ART_WINDOW_TOP } from './spriteEngine';

// Matches the ~631px-wide art window used on the real print template PSD (that
// window is 289 logical units wide there), applied uniformly to the whole card so
// background, sprites and frame all line up at that same real-world scale.
const PIXEL_RATIO = 631 / CARD_W;

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

// Background and frame art are both pre-composited by the artist at the same full
// card canvas, so both render identically: scaled up to the export's full-card
// size, no cropping needed — the frame's own transparent die-cut window does the
// clipping, exactly like .card-template-overlay does live (see PokemonCard.css).
const renderFullCardLayer = (imgEl, width, height) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  const naturalW = imgEl.naturalWidth || width;
  const naturalH = imgEl.naturalHeight || height;
  ctx.drawImage(imgEl, 0, 0, naturalW, naturalH, 0, 0, width, height);
  return canvas;
};

// Rasterizes one sprite (with its flip applied) onto a canvas sized to its
// bounding box, positioned in full-card space — sprite.x/y are logical coords
// within the art window, which itself sits at (ART_WINDOW_LEFT, ART_WINDOW_TOP)
// inside the full card, so that offset is added before scaling by pixelRatio.
const renderSpriteLayer = (sprite, imgEl, pixelRatio) => {
  const w = sprite.w * pixelRatio;
  const h = sprite.h * pixelRatio;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');

  // Match the live card preview's object-fit: contain — scale the image to
  // fit inside the box preserving its own aspect ratio, rather than stretching
  // it to fill the box (which distorts sprites whose box isn't the same
  // proportions as their art, e.g. a square box around tall trainer art).
  const naturalW = imgEl.naturalWidth || w;
  const naturalH = imgEl.naturalHeight || h;
  const scale = Math.min(w / naturalW, h / naturalH);
  const drawW = naturalW * scale;
  const drawH = naturalH * scale;

  ctx.translate(w / 2, h / 2);
  if (sprite.flip) ctx.scale(-1, 1);
  ctx.drawImage(imgEl, -drawW / 2, -drawH / 2, drawW, drawH);

  const left = Math.round(((ART_WINDOW_LEFT + sprite.x) * pixelRatio) - w / 2);
  const top = Math.round(((ART_WINDOW_TOP + sprite.y) * pixelRatio) - h / 2);
  return { canvas, left, top };
};

// Loads the shared background/frame/sprite layers once so both exports below can
// reuse the same rasterized canvases instead of loading/drawing everything twice.
const buildLayers = async (artLayerEl, sprites, bgUrl, frameUrl) => {
  const width = Math.round(FULL_CARD_W * PIXEL_RATIO);
  const height = Math.round(FULL_CARD_H * PIXEL_RATIO);

  const [bgImgEl, frameImgEl] = await Promise.all([
    bgUrl ? loadImage(bgUrl) : null,
    frameUrl ? loadImage(frameUrl) : null,
  ]);

  const spriteLayers = [...sprites]
    .sort((a, b) => a.z - b.z)
    .map(sprite => {
      const imgEl = artLayerEl.querySelector(`[data-sprite-id="${sprite.id}"] img`);
      if (!imgEl) return null;
      return { sprite, ...renderSpriteLayer(sprite, imgEl, PIXEL_RATIO) };
    })
    .filter(Boolean);

  return {
    width, height,
    bgCanvas: bgImgEl ? renderFullCardLayer(bgImgEl, width, height) : null,
    frameCanvas: frameImgEl ? renderFullCardLayer(frameImgEl, width, height) : null,
    spriteLayers,
  };
};

// Builds the full layered PSD as a Blob. `artLayerEl` is the live .card-art-layer
// DOM node (holds one wrapper div per sprite, each tagged with data-sprite-id —
// see DraggableSprite.js). `bgUrl`/`frameUrl` are the clean background/frame
// source URLs, loaded fresh here at full resolution.
export const buildCardPsdBlob = async (artLayerEl, sprites, bgUrl, frameUrl) => {
  const { width, height, bgCanvas, frameCanvas, spriteLayers } =
    await buildLayers(artLayerEl, sprites, bgUrl, frameUrl);

  const children = [];
  // ag-psd writes `children` into the PSD's layer records bottom-to-top (first =
  // bottom-most), so Background goes first, sprites in ascending z, Frame last.
  if (bgCanvas) children.push({ name: 'Background', left: 0, top: 0, canvas: bgCanvas });
  for (const { sprite, canvas, left, top } of spriteLayers) {
    const label = sprite.kind === 'trainer' ? 'Trainer' : 'Pokemon';
    children.push({ name: `${label}: ${fmtName(sprite.name)}`, left, top, canvas });
  }
  if (frameCanvas) children.push({ name: 'Card Frame', left: 0, top: 0, canvas: frameCanvas });

  const psd = { width, height, children };
  const buffer = writePsd(psd);
  return new Blob([buffer], { type: 'image/vnd.adobe.photoshop' });
};

// Builds a single flattened full-card PNG (background + sprites + frame baked
// together) as a data URL — the customer-facing design snapshot. Composited the
// same way as the PSD layers above rather than DOM-capturing the live preview,
// since the live preview intentionally shows the watermarked background/frame
// while this export needs the clean ones.
export const buildCardPngDataUrl = async (artLayerEl, sprites, bgUrl, frameUrl) => {
  const { width, height, bgCanvas, frameCanvas, spriteLayers } =
    await buildLayers(artLayerEl, sprites, bgUrl, frameUrl);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  if (bgCanvas) ctx.drawImage(bgCanvas, 0, 0);
  for (const { canvas: spriteCanvas, left, top } of spriteLayers) {
    ctx.drawImage(spriteCanvas, left, top);
  }
  if (frameCanvas) ctx.drawImage(frameCanvas, 0, 0);

  return canvas.toDataURL('image/png');
};
